import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusChip, cad, day, errMsg } from "@/components/sponsored/format";

function Decide({ rv, approve, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [voidSale, setVoidSale] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/performance-fee-reviews/${rv.id}/decide`, { decision: approve ? "approve" : "reject", note, void_sale: approve && voidSale });
      toast.success(approve ? "Fee reversed" : "Review declined");
      onDone();
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "That didn't work"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`admin-fee-${approve ? "approve" : "reject"}-dialog`}>
        <DialogHeader>
          <DialogTitle>{approve ? "Reverse this Performance Fee" : "Decline this review"}</DialogTitle>
          <DialogDescription>Order {rv.order_id} · fee {cad(rv.fee_cad)}. {approve ? "Reversal is for duplicates, fraud, uncaptured payments or system/attribution errors only." : "The fee stays in place."}</DialogDescription>
        </DialogHeader>
        <textarea className="pf-input w-full min-h-[80px]" maxLength={2000} placeholder="Decision note shown to the merchant" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-fee-note-input" />
        {approve && rv.sale_status === "verified" && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <Checkbox checked={voidSale} onCheckedChange={(v) => setVoidSale(!!v)} data-testid="admin-fee-void-checkbox" />
            Also void the sale (reverses the shopper's cashback)
          </label>
        )}
        <DialogFooter>
          <button onClick={go} disabled={busy} className="pf-btn-primary px-5 py-2 rounded-full text-sm disabled:opacity-50" data-testid={`admin-fee-${approve ? "approve" : "reject"}-confirm`}>{busy ? "Working…" : approve ? "Reverse fee" : "Decline"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminFeeReviews({ sa }) {
  const [d, setD] = useState(null);
  const [act, setAct] = useState(null);
  const load = useCallback(() => api.get("/admin/performance-fee-reviews").then((r) => setD(r.data)).catch(() => setD({ rows: [], reasons: {} })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-slate-500">Loading…</p>;
  if (d.enabled === false) return <p className="text-sm text-slate-500" data-testid="admin-fee-reviews-off">The Performance Fee refund policy is switched off.</p>;
  if (!d.rows.length) return <p className="text-sm text-slate-400 italic" data-testid="admin-fee-reviews-empty">No fee review requests yet.</p>;
  return (
    <div className="space-y-3" data-testid="admin-fee-reviews">
      {!sa && <p className="text-xs text-amber-700">Only a super admin can decide fee reviews.</p>}
      {d.rows.map((r) => (
        <div key={r.id} className="rounded-2xl border border-slate-200 bg-white/70 p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`admin-fee-row-${r.id}`}>
          <div className="text-sm max-w-2xl">
            <div className="font-semibold text-slate-900">{r.store_name || r.store_id} · order <span className="font-mono">{r.order_id}</span> · {cad(r.fee_cad)}</div>
            <div className="text-xs text-slate-500">{d.reasons[r.reason] || r.reason} · requested {day(r.requested_at)} · sale {r.sale_status}</div>
            {r.details && <p className="text-sm text-slate-700 mt-1">{r.details}</p>}
            {r.decision_note && <p className="text-xs text-slate-600 mt-1">Decision: {r.decision_note}{r.void_sale ? " · sale voided" : ""}</p>}
          </div>
          <div className="flex items-center gap-2">
            <StatusChip status={r.status} testid={`admin-fee-status-${r.id}`} />
            {sa && r.status === "REQUESTED" && <>
              <button onClick={() => setAct({ rv: r, approve: true })} className="pf-btn-primary px-4 py-1.5 rounded-full text-xs" data-testid={`admin-fee-approve-${r.id}`}>Reverse fee</button>
              <button onClick={() => setAct({ rv: r, approve: false })} className="pf-btn-glass px-4 py-1.5 rounded-full text-xs font-semibold" data-testid={`admin-fee-reject-${r.id}`}>Decline</button>
            </>}
          </div>
        </div>
      ))}
      {act && <Decide {...act} onClose={() => setAct(null)} onDone={load} />}
    </div>
  );
}
